import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Zap, Trophy, Clock, Users, Star, Plus, Filter, ChevronDown } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Tous', 'Prévention & Droits', 'Santé & Bien-être', 'Famille & Éducation', 'Compétences pro', 'Culture & Société', 'Entreprise'];
const DIFFICULTIES = ['Tous', 'debutant', 'intermediaire', 'expert'];
const DIFF_LABELS = { debutant: 'Débutant', intermediaire: 'Intermédiaire', expert: 'Expert', adaptatif: 'Adaptatif' };
const DIFF_COLORS = { debutant: 'text-[#06d6a0] bg-[#06d6a0]/10', intermediaire: 'text-[#ffd93d] bg-[#ffd93d]/10', expert: 'text-[#ff6b6b] bg-[#ff6b6b]/10', adaptatif: 'text-purple-400 bg-purple-400/10' };

export default function QuizBrowse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tous');
  const [difficulty, setDifficulty] = useState('Tous');
  const [pinInput, setPinInput] = useState('');
  const [myQuizzes, setMyQuizzes] = useState(false);

  const fetchQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category !== 'Tous') params.set('category', category);
      if (difficulty !== 'Tous') params.set('difficulty', difficulty);
      if (myQuizzes) params.set('my_quizzes', 'true');
      const [qRes, featRes] = await Promise.all([
        axios.get(`${API}/api/quiz?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/quiz/featured`, { withCredentials: true }),
      ]);
      setQuizzes(qRes.data.quizzes || []);
      setFeatured(featRes.data);
    } catch (e) {
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  }, [search, category, difficulty, myQuizzes]);

  useEffect(() => { fetchQuizzes(); }, [fetchQuizzes]);

  const handleJoinPin = (e) => {
    e.preventDefault();
    if (pinInput.trim().length === 6) navigate(`/quiz/${pinInput.trim()}/live`);
  };

  return (
    <Layout>
      <Helmet>
        <title>Quiz Kurso — Testez vos connaissances</title>
        <meta name="description" content="Parcourez les quiz Kurso, jouez en solo ou rejoignez une session multijoueur en direct." />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">Quiz Kurso</h1>
            <p className="text-[#a0a0ab] text-sm mt-0.5">Testez vos connaissances, défiez la communauté</p>
          </div>
          <div className="flex gap-2">
            {user && (
              <button onClick={() => navigate('/quiz/create')} className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5" data-testid="create-quiz-btn">
                <Plus size={15} /> Créer un quiz
              </button>
            )}
          </div>
        </div>

        {/* Join PIN */}
        <div className="bg-gradient-to-r from-[#ff6b6b]/10 to-[#06d6a0]/10 border border-white/10 rounded-2xl p-4 mb-6">
          <p className="text-white text-sm font-medium mb-2">Rejoindre une session avec un code PIN</p>
          <form onSubmit={handleJoinPin} className="flex gap-2">
            <input
              type="text"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              className="flex-1 max-w-xs bg-white/5 border border-white/10 text-white text-xl font-mono text-center rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#ff6b6b]/50 tracking-widest"
              data-testid="pin-input"
            />
            <button type="submit" disabled={pinInput.length !== 6} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50">
              Rejoindre
            </button>
          </form>
        </div>

        {/* Featured Quiz */}
        {featured && (
          <div className="bg-gradient-to-r from-[#ffd93d]/10 to-[#ff6b6b]/10 border border-[#ffd93d]/30 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Star size={14} className="text-[#ffd93d]" fill="#ffd93d" />
              <span className="text-[#ffd93d] text-xs font-semibold uppercase tracking-wide">Quiz de la semaine</span>
            </div>
            <h2 className="text-lg font-bold text-white mb-1">{featured.title}</h2>
            <div className="flex items-center gap-3 text-xs text-[#a0a0ab] mb-3">
              <span>{featured.num_questions} questions</span>
              <span className={`px-2 py-0.5 rounded-full ${DIFF_COLORS[featured.difficulty] || ''}`}>{DIFF_LABELS[featured.difficulty] || featured.difficulty}</span>
              <span className="flex items-center gap-1"><Clock size={11} /> {featured.estimated_duration} min</span>
              <span className="flex items-center gap-1"><Trophy size={11} /> {featured.play_count || 0} parties</span>
            </div>
            <button onClick={() => navigate(`/quiz/${featured.quiz_id}/play`)} className="btn-primary text-sm px-5 py-2">
              Jouer maintenant
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un quiz..."
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
            />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}
            className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none">
            {DIFFICULTIES.map(d => <option key={d} value={d}>{d === 'Tous' ? 'Tous niveaux' : DIFF_LABELS[d] || d}</option>)}
          </select>
          {user && (
            <button onClick={() => setMyQuizzes(!myQuizzes)}
              className={`text-sm px-3 py-2 rounded-xl border transition-colors ${myQuizzes ? 'bg-[#ff6b6b]/20 border-[#ff6b6b]/50 text-[#ff6b6b]' : 'bg-white/5 border-white/10 text-[#a0a0ab] hover:text-white'}`}>
              Mes quiz
            </button>
          )}
        </div>

        {/* Quiz Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : quizzes.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-3">🧠</div>
            <p className="text-[#a0a0ab]">Aucun quiz trouvé</p>
            {user && <button onClick={() => navigate('/quiz/create')} className="btn-primary text-sm mt-3 px-4 py-2">Créer le premier !</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quizzes.map(quiz => <QuizCard key={quiz.id} quiz={quiz} onPlay={() => navigate(`/quiz/${quiz.quiz_id}/play`)} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}

function QuizCard({ quiz, onPlay }) {
  const avg_rating = quiz.rating_count > 0 ? (quiz.rating_sum / quiz.rating_count).toFixed(1) : null;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:border-[#ff6b6b]/30 transition-colors cursor-pointer group" onClick={onPlay} data-testid="quiz-card">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${DIFF_COLORS[quiz.difficulty] || 'text-[#a0a0ab]'}`}>
          {DIFF_LABELS[quiz.difficulty] || quiz.difficulty}
        </span>
        {avg_rating && (
          <span className="flex items-center gap-1 text-xs text-[#ffd93d]">
            <Star size={10} fill="#ffd93d" /> {avg_rating}
          </span>
        )}
      </div>
      <h3 className="text-white font-semibold text-sm mb-1 group-hover:text-[#ff6b6b] transition-colors">{quiz.title}</h3>
      <p className="text-[#a0a0ab] text-xs mb-3 truncate">{quiz.topic}</p>
      <div className="flex items-center gap-3 text-xs text-[#a0a0ab]">
        <span className="flex items-center gap-1"><Zap size={10} /> {quiz.num_questions} q.</span>
        <span className="flex items-center gap-1"><Clock size={10} /> {quiz.estimated_duration} min</span>
        <span className="flex items-center gap-1"><Trophy size={10} /> {quiz.play_count || 0}</span>
      </div>
      <div className="mt-2 text-xs text-[#a0a0ab] truncate">{quiz.category}</div>
    </div>
  );
}
